/**
 * useAddItemForm - Form state and validation logic for AddItemModal
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import type { Cliente, ProdutoCliente } from '../services/clienteService';
import clienteService from '../services/clienteService';

export interface AddItemFormData {
  cliente_id: number;
  com_regime: boolean | null;
  codigo_produto: string;
  descricao: string;
  lote: string;
  data_validade: string; // YYYY-MM-DD format for API
  quantidade: number;
  quantidade_contada: number | null; // N46: opcional; null = não informado
  quantidade_coletar: number;
}

interface UseAddItemFormOptions {
  isOpen: boolean;
  jobClientes: Cliente[];
}

function isValidDate(dateStr: string): boolean {
  if (!dateStr || dateStr.length !== 10) return false;
  const parts = dateStr.split('/');
  if (parts.length !== 3) return false;
  const day = parseInt(parts[0], 10);
  const month = parseInt(parts[1], 10);
  const year = parseInt(parts[2], 10);
  if (isNaN(day) || isNaN(month) || isNaN(year)) return false;
  if (month < 1 || month > 12) return false;
  if (day < 1 || day > 31) return false;
  if (year < 2000 || year > 2100) return false;
  const daysInMonth = new Date(year, month, 0).getDate();
  if (day > daysInMonth) return false;
  return true;
}

const DEFAULT_FORM: AddItemFormData = {
  cliente_id: 0,
  com_regime: null,
  codigo_produto: '',
  descricao: '',
  lote: '',
  data_validade: '',
  quantidade: 1,
  quantidade_contada: null,
  quantidade_coletar: 0,
};

export function useAddItemForm({ isOpen, jobClientes }: UseAddItemFormOptions) {
  const [formData, setFormData] = useState<AddItemFormData>(DEFAULT_FORM);
  const [error, setError] = useState<string | null>(null);
  const [showRegimeSelector, setShowRegimeSelector] = useState(false);
  const [dateInput, setDateInput] = useState('');

  // Product search state
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<ProdutoCliente[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<ProdutoCliente | null>(null);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const loadAllProducts = useCallback(async (clienteId: number) => {
    if (!clienteId) { setSearchResults([]); return; }
    setIsSearching(true);
    try {
      const results = await clienteService.listProdutosCliente(clienteId);
      setSearchResults(results);
    } catch (err) {
      console.error('Erro ao carregar produtos:', err);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  }, []);

  const searchProducts = useCallback(async (term: string, clienteId: number) => {
    if (!clienteId) { setSearchResults([]); setShowDropdown(false); return; }
    setIsSearching(true);
    try {
      const results = await clienteService.listProdutosCliente(clienteId, term || undefined);
      setSearchResults(results);
      setShowDropdown(results.length > 0);
    } catch (err) {
      console.error('Erro ao buscar produtos:', err);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  }, []);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      const defaultClienteId = jobClientes.length === 1 ? jobClientes[0].id : 0;
      setFormData({ ...DEFAULT_FORM, cliente_id: defaultClienteId });
      setError(null);
      setSearchTerm('');
      setSearchResults([]);
      setSelectedProduct(null);
      setDateInput('');
      setShowDropdown(false);

      if (jobClientes.length === 1) {
        const cliente = jobClientes[0];
        if (cliente.com_regime && cliente.sem_regime) {
          setShowRegimeSelector(true);
        } else {
          setShowRegimeSelector(false);
          setFormData(prev => ({ ...prev, com_regime: cliente.com_regime ? true : false }));
        }
        loadAllProducts(defaultClienteId);
      }
    }
  }, [isOpen, jobClientes, loadAllProducts]);

  const handleSearchChange = useCallback((value: string) => {
    setSearchTerm(value);
    setSelectedProduct(null);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      searchProducts(value, formData.cliente_id);
    }, 300);
  }, [formData.cliente_id, searchProducts]);

  const handleSelectProduct = useCallback((product: ProdutoCliente) => {
    setSelectedProduct(product);
    setSearchTerm(`${product.codigo} - ${product.descricao_completa}`);
    setFormData(prev => ({ ...prev, codigo_produto: product.codigo, descricao: product.descricao_completa }));
    setShowDropdown(false);
  }, []);

  const handleClienteChange = useCallback((clienteId: number) => {
    const cliente = jobClientes.find(c => c.id === clienteId);
    let comRegime: boolean | null = null;
    let needsRegimeSelector = false;
    if (cliente) {
      if (cliente.com_regime && !cliente.sem_regime) comRegime = true;
      else if (!cliente.com_regime && cliente.sem_regime) comRegime = false;
      else if (cliente.com_regime && cliente.sem_regime) needsRegimeSelector = true;
    }
    setFormData(prev => ({ ...prev, cliente_id: clienteId, com_regime: comRegime, codigo_produto: '', descricao: '' }));
    setShowRegimeSelector(needsRegimeSelector);
    setSearchTerm('');
    setSelectedProduct(null);
    if (clienteId) loadAllProducts(clienteId);
    else setSearchResults([]);
  }, [jobClientes, loadAllProducts]);

  const handleDateChange = useCallback((value: string) => {
    const numbers = value.replace(/\D/g, '');
    let formatted = '';
    if (numbers.length > 0) {
      formatted = numbers.slice(0, 2);
      if (numbers.length > 2) formatted += '/' + numbers.slice(2, 4);
      if (numbers.length > 4) formatted += '/' + numbers.slice(4, 8);
    }
    setDateInput(formatted);
    if (numbers.length === 8) {
      const day = numbers.slice(0, 2);
      const month = numbers.slice(2, 4);
      const year = numbers.slice(4, 8);
      setFormData(prev => ({ ...prev, data_validade: `${year}-${month}-${day}` }));
    } else {
      setFormData(prev => ({ ...prev, data_validade: '' }));
    }
  }, []);

  const validate = useCallback((): string | null => {
    if (formData.cliente_id === 0) return 'Selecione um cliente';
    if (showRegimeSelector && formData.com_regime === null) return 'Selecione o regime tributário';
    if (!formData.codigo_produto.trim()) return 'Selecione um produto da lista';
    if (!formData.descricao.trim()) return 'Descrição é obrigatória';
    if (!formData.lote.trim()) return 'Lote é obrigatório';
    if (!isValidDate(dateInput)) return 'Data de validade inválida. Use o formato DD/MM/AAAA';
    if (!formData.data_validade) return 'Data de validade é obrigatória';
    // N46: sistema 0 é válido (sobra — material fora da base)
    if (formData.quantidade < 0) return 'Quantidade não pode ser negativa';
    if (formData.quantidade_contada !== null && formData.quantidade_contada < 0) {
      return 'Quantidade contada não pode ser negativa';
    }
    return null;
  }, [formData, showRegimeSelector, dateInput]);

  return {
    formData,
    setFormData,
    error,
    setError,
    showRegimeSelector,
    dateInput,
    searchTerm,
    searchResults,
    isSearching,
    showDropdown,
    setShowDropdown,
    selectedProduct,
    dropdownRef,
    handleSearchChange,
    handleSelectProduct,
    handleClienteChange,
    handleDateChange,
    validate,
  };
}
